import jsPDF from "jspdf";
import "jspdf-autotable";

interface ReportData {
  product_category: string;
  marketplace: string;
  created_at: string;
  themes: any[];
  desired_outcomes: string[];
  objections: any[];
  copy_pack: any;
  sources: string[];
  review_evidence_available?: boolean;
  seller_claims_examples?: string[];
  evidence_sources_used?: any[];
  template_type?: string;
  topic?: string | null;
  industry_context?: string | null;
  market_radar_enabled?: boolean;
  baseline_report_id?: string | null;
  delta_summary?: string | null;
  delta?: any;
  price_radar_enabled?: boolean;
  price_radar?: any;
}

function sanitize(text: string): string {
  if (!text) return "";
  return String(text)
    .replace(/[\u2705\u2611\u2714]/g, "•")
    .replace(/[\u274C\u274E\u2716]/g, "-")
    .replace(/[\u27A1\u2192\u25B6]/g, "-")
    .replace(/[\u2B50\u2B55\u26A0\u2757\u2753\u2049\u203C]/g, "")
    .replace(/[\u{1F000}-\u{1FFFF}]/gu, "")
    .replace(/[\u200B-\u200F\u2028-\u202F\uFEFF]/g, "")
    .replace(/\u2014/g, " -- ")
    .replace(/\u2013/g, "-")
    .replace(/[^\x20-\x7E\xA0-\xFF\n\r\t]/g, "");
}

export function generateReportPdf(report: ReportData) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 40;
  const footerHeight = 30;
  const usableBottom = pageHeight - margin - footerHeight;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  const ensureSpace = (needed: number) => {
    if (y + needed > usableBottom) {
      doc.addPage();
      y = margin;
    }
  };

  const heading = (text: string, size = 16) => {
    ensureSpace(60);
    doc.setFontSize(size).setFont("helvetica", "bold");
    doc.text(sanitize(text), margin, y);
    y += size + 8;
    doc.setFont("helvetica", "normal");
  };

  const paragraph = (text: string, size = 10) => {
    doc.setFontSize(size);
    const lines = doc.splitTextToSize(sanitize(text), contentWidth);
    ensureSpace(lines.length * (size + 4));
    doc.text(lines, margin, y);
    y += lines.length * (size + 4) + 4;
  };

  const bullet = (text: string) => {
    doc.setFontSize(10);
    const textX = margin + 16;
    const lines = doc.splitTextToSize(sanitize(text), contentWidth - 20);
    ensureSpace(lines.length * 14);
    doc.text("\u2022", margin + 4, y);
    doc.text(lines, textX, y);
    y += lines.length * 14;
  };

  const sectionBreak = () => {
    if (y > margin + 80) {
      doc.addPage();
      y = margin;
    }
  };

  const templateType = report.template_type || "Amazon listing copy pack";
  const isGenericTemplate = templateType === "Generic marketing messaging pack";
  const evidenceSourcesUsed = report.evidence_sources_used || [];
  const hasWebEvidence = evidenceSourcesUsed.some((s: any) => ["reddit", "youtube", "web", "competitor_website"].includes(s.type));
  const hasYoutubeWithoutComments = evidenceSourcesUsed.some((s: any) => s.type === "youtube" && s.note);

  // ── Header ──
  doc.setFontSize(22).setFont("helvetica", "bold");
  doc.text(isGenericTemplate ? "Marketing Messaging Pack" : "Conversion Copy Pack", margin, y);
  y += 28;
  doc.setFontSize(11).setFont("helvetica", "normal");
  const dateStr = new Date(report.created_at).toLocaleDateString();
  doc.text(
    sanitize(`${templateType}  \u2022  ${report.product_category}  \u2022  Generated ${dateStr}`),
    margin, y
  );
  y += 20;

  // Topic + Industry context
  if (report.topic || report.industry_context) {
    doc.setFontSize(10).setFont("helvetica", "italic");
    const parts: string[] = [];
    if (report.topic) parts.push(`Topic: ${report.topic}`);
    if (report.industry_context) parts.push(`Industry/context: ${report.industry_context}`);
    doc.text(sanitize(parts.join("  |  ")), margin, y);
    y += 16;
    doc.setFont("helvetica", "normal");
  }

  doc.setDrawColor(200);
  doc.line(margin, y, pageWidth - margin, y);
  y += 16;

  // ── Summary ──
  heading("Summary");
  const pack = report.copy_pack;
  if (pack?.summary) {
    paragraph(pack.summary);
  } else {
    const sources = report.sources || [];
    const themes = report.themes || [];
    const objections = report.objections || [];
    const themeType = pack?.theme_type || "pain_themes";
    const uvCount = evidenceSourcesUsed.filter((s: any) => s.category === "user_voice").length;
    const summaryEnding = report.review_evidence_available === false
      ? "based on competitor listing claims (reviews could not be accessed)"
      : themeType === "market_themes"
      ? "from market analysis and competitive research"
      : uvCount >= 2
      ? "from real user feedback across the web"
      : "based on a mix of editorial guidance and limited user voice sources";
    paragraph(
      `Analysis of ${sources.length} sources in the "${report.product_category}" category on Amazon ${report.marketplace} revealed ${themes.length} key ${themeType === "market_themes" ? "market" : "pain"} themes and ${objections.length} common objections ${summaryEnding}.`
    );
  }
  y += 4;

  // ── Evidence used ──
  const hasPastedReviews = evidenceSourcesUsed.some((s: any) => s.type === "amazon_pasted");
  ensureSpace(20);
  doc.setFontSize(9).setFont("helvetica", "italic");
  const evidenceParts: string[] = [];
  if (hasPastedReviews) evidenceParts.push("Amazon low-star reviews (user-pasted)");
  if (hasWebEvidence) evidenceParts.push("Web chatter");
  const evidenceLabel = "Evidence used: " + (evidenceParts.length > 0 ? evidenceParts.join(" + ") : "Web chatter only");
  doc.text(sanitize(evidenceLabel), margin, y);
  y += 16;
  doc.setFont("helvetica", "normal");

  // ── Market Radar ──
  if (report.market_radar_enabled && report.delta_summary) {
    heading("Market Radar: What changed since last run");
    paragraph(report.delta_summary);
    // Add tip when no baseline existed
    if (!(report as any).baseline_report_id) {
      doc.setFontSize(9).setFont("helvetica", "italic");
      const tipLines = doc.splitTextToSize("Tip: run again tomorrow or next week to see what changed.", contentWidth);
      ensureSpace(tipLines.length * 13);
      doc.text(tipLines, margin, y);
      y += tipLines.length * 13 + 4;
      doc.setFont("helvetica", "normal");
    }
    if (report.delta) {
      const deltaObj = report.delta as any;
      const deltaSections = [
        { label: "New pain themes", key: "new_pain_themes" },
        { label: "Rising pain themes", key: "rising_pain_themes" },
        { label: "Falling pain themes", key: "falling_pain_themes" },
        { label: "New objections", key: "new_objections" },
        { label: "New language buyers use", key: "new_language_buyers_use" },
        { label: "New competitor claims", key: "new_competitor_claims" },
      ];
      for (const { label, key } of deltaSections) {
        const items = deltaObj[key] as string[] | undefined;
        if (items?.length) {
          ensureSpace(20);
          doc.setFontSize(11).setFont("helvetica", "bold");
          doc.text(sanitize(label + ":"), margin, y);
          y += 16;
          doc.setFont("helvetica", "normal");
          items.forEach((item: string) => bullet(item));
          y += 4;
        }
      }
    }
    // Next action callout
    ensureSpace(20);
    doc.setFontSize(9).setFont("helvetica", "italic");
    const actionLine = doc.splitTextToSize("Next action: update your listing/ad copy using the New + Rising themes, then re-run weekly to stay current.", contentWidth);
    doc.text(actionLine, margin, y);
    y += actionLine.length * 13 + 4;
    doc.setFont("helvetica", "normal");
    y += 8;
  }

  // ── Retail Price Radar ──
  if (report.price_radar_enabled && report.price_radar) {
    const pr = report.price_radar;
    sectionBreak();
    heading("Retail Price Radar (web snapshot)");
    if (pr.price_range) {
      paragraph(`Range: ${pr.price_range.currency}${pr.price_range.min} – ${pr.price_range.currency}${pr.price_range.max}`);
    }
    if (pr.common_band) {
      paragraph(`Most common band: ${pr.common_band}`);
    }
    if (pr.median_price && pr.median_price > 0) {
      paragraph(`Median: ~${pr.price_range?.currency || "£"}${pr.median_price}`);
    }
    if (pr.low_vs_high) {
      ensureSpace(30);
      doc.setFontSize(11).setFont("helvetica", "bold");
      doc.text("Lower vs higher prices:", margin, y);
      y += 16;
      doc.setFont("helvetica", "normal");
      paragraph(pr.low_vs_high);
    }
    if (pr.pricing_signals?.length > 0) {
      ensureSpace(20);
      doc.setFontSize(11).setFont("helvetica", "bold");
      doc.text("Pricing signals:", margin, y);
      y += 16;
      doc.setFont("helvetica", "normal");
      pr.pricing_signals.forEach((s: string) => bullet(s));
      y += 4;
    }
    if (pr.source_urls?.length > 0) {
      ensureSpace(20);
      doc.setFontSize(11).setFont("helvetica", "bold");
      doc.text("Sources:", margin, y);
      y += 16;
      doc.setFont("helvetica", "normal");
      pr.source_urls.forEach((u: string, i: number) => {
        ensureSpace(28);
        let domain = "";
        try { domain = new URL(u).hostname.replace("www.", ""); } catch { domain = u.slice(0, 40); }
        // Line 1: domain (10pt bold)
        doc.setFontSize(10).setFont("helvetica", "bold");
        const label = `${i + 1}. ${domain}`;
        doc.text(sanitize(label), margin, y);
        y += 13;
        // Line 2: full URL (7pt grey)
        doc.setFontSize(7).setFont("helvetica", "normal").setTextColor(120, 120, 120);
        const urlLines = doc.splitTextToSize(sanitize(u), contentWidth - 16);
        doc.text(urlLines, margin + 8, y);
        y += urlLines.length * 10 + 4;
        doc.setTextColor(0, 0, 0);
      });
    }
    ensureSpace(30);
    doc.setFontSize(8).setFont("helvetica", "italic");
    const disclaimerText = sanitize(pr.disclaimer || "Retail prices are a public web snapshot at the time of analysis and may change. Always verify on the seller's site before setting final pricing.");
    const disclaimerLines = doc.splitTextToSize(disclaimerText, contentWidth);
    doc.text(disclaimerLines, margin, y);
    y += disclaimerLines.length * 12 + 8;
    doc.setFont("helvetica", "normal");
  }

  if (isGenericTemplate && pack) {
    // ── Generic Template Sections ──

    // Buyer-experience warning
    if (typeof pack.buyer_experience_ratio === "number" && pack.buyer_experience_ratio < 60) {
      ensureSpace(30);
      doc.setFontSize(10).setFont("helvetica", "italic");
      const warnText = sanitize("Note: Evidence may be informational rather than buyer experience -- refine keywords or context.");
      const warnLines = doc.splitTextToSize(warnText, contentWidth);
      doc.text(warnLines, margin, y);
      y += warnLines.length * 14 + 8;
      doc.setFont("helvetica", "normal");
    }

    // Positioning
    if (pack.positioning_statement) {
      heading("Positioning");
      paragraph(pack.positioning_statement);
      y += 6;
    }

    // Key Pains
    if (pack.key_pains?.length > 0) {
      heading("Key Pains (Buyer Language)");
      pack.key_pains.forEach((p: string) => bullet(p));
      y += 6;
    }

    // Top Objections
    if (pack.top_objections?.length > 0) {
      sectionBreak();
      heading("Top Objections");
      pack.top_objections.forEach((o: any) => {
        ensureSpace(70);
        doc.setFontSize(10).setFont("helvetica", "bold");
        doc.text("Objection:", margin, y);
        doc.setFont("helvetica", "normal");
        const objLines = doc.splitTextToSize(sanitize(o.objection), contentWidth - 80);
        doc.text(objLines, margin + 70, y);
        y += objLines.length * 14 + 4;
        doc.setFont("helvetica", "bold");
        doc.text("Rebuttal:", margin, y);
        doc.setFont("helvetica", "normal");
        const rebLines = doc.splitTextToSize(sanitize(o.rebuttal), contentWidth - 80);
        doc.text(rebLines, margin + 70, y);
        y += rebLines.length * 14 + 12;
      });
    }

    // Reassurance Angles
    if (pack.reassurance_angles?.length > 0) {
      heading("Reassurance Angles");
      pack.reassurance_angles.forEach((r: string) => bullet(r));
      y += 6;
    }

    // Message Pillars
    if (pack.message_pillars?.length > 0) {
      heading("Message Pillars");
      pack.message_pillars.forEach((m: string) => bullet(m));
      y += 6;
    }

    // Hooks
    if (pack.hooks?.length > 0) {
      sectionBreak();
      heading("Hooks");
      pack.hooks.forEach((h: string, i: number) => bullet(`${i + 1}. ${h}`));
      y += 6;
    }

    // Ad Angles
    if (pack.ad_angles?.length > 0) {
      sectionBreak();
      heading("Ad Angles");
      pack.ad_angles.forEach((ad: any, i: number) => {
        ensureSpace(60);
        doc.setFontSize(12).setFont("helvetica", "bold");
        doc.text(sanitize(`${i + 1}. ${ad.angle_name}`), margin, y);
        y += 18;
        doc.setFont("helvetica", "normal");
        paragraph(ad.description);
        y += 4;
      });
    }

    // FAQ
    if (pack.faqs?.length > 0) {
      sectionBreak();
      heading("FAQ");
      pack.faqs.forEach((f: any) => {
        ensureSpace(40);
        doc.setFontSize(10).setFont("helvetica", "bold");
        const qLines = doc.splitTextToSize(sanitize(`Q: ${f.q}`), contentWidth);
        doc.text(qLines, margin, y);
        y += qLines.length * 14;
        doc.setFont("helvetica", "normal");
        const aLines = doc.splitTextToSize(sanitize(`A: ${f.a}`), contentWidth);
        ensureSpace(aLines.length * 14);
        doc.text(aLines, margin, y);
        y += aLines.length * 14 + 8;
      });
    }

    // Do/Don't Claims
    if (pack.do_dont_claims) {
      sectionBreak();
      heading("Do / Don't Claims");
      if (pack.do_dont_claims.do?.length > 0) {
        ensureSpace(20);
        doc.setFontSize(11).setFont("helvetica", "bold");
        doc.text("Safe to claim:", margin, y);
        y += 16;
        doc.setFont("helvetica", "normal");
        pack.do_dont_claims.do.forEach((c: string) => bullet(c));
        y += 6;
      }
      if (pack.do_dont_claims.dont?.length > 0) {
        ensureSpace(20);
        doc.setFontSize(11).setFont("helvetica", "bold");
        doc.text("Avoid claiming:", margin, y);
        y += 16;
        doc.setFont("helvetica", "normal");
        pack.do_dont_claims.dont.forEach((c: string) => bullet(c));
        y += 6;
      }
    }
  } else {
    // ── Amazon Copy Pack Template ──

    // Review evidence warning
    if (report.review_evidence_available === false) {
      ensureSpace(40);
      doc.setFontSize(10).setFont("helvetica", "italic");
      const warnText = sanitize("Review text not accessible -- report based on listing claims.");
      const warnLines = doc.splitTextToSize(warnText, contentWidth);
      doc.text(warnLines, margin, y);
      y += warnLines.length * 14 + 8;
      doc.setFont("helvetica", "normal");
    }

    // YouTube disclaimer
    if (hasYoutubeWithoutComments) {
      ensureSpace(30);
      doc.setFontSize(9).setFont("helvetica", "italic");
      const ytNote = sanitize("Note: YouTube evidence from descriptions/pages (comments not accessible).");
      const ytLines = doc.splitTextToSize(ytNote, contentWidth);
      doc.text(ytLines, margin, y);
      y += ytLines.length * 13 + 4;
      doc.setFont("helvetica", "normal");
    }

    // Amazon pasted insights
    const pastedPack = report.copy_pack;
    if (pastedPack?.amazon_pasted_insights) {
      const pi = pastedPack.amazon_pasted_insights;
      heading("Amazon low-star themes (from pasted reviews)");
      (pi.themes || []).slice(0, 5).forEach((t: any, i: number) => {
        ensureSpace(50);
        doc.setFontSize(12).setFont("helvetica", "bold");
        doc.text(sanitize(`${i + 1}. ${t.title}`), margin, y);
        y += 18;
        doc.setFont("helvetica", "normal");
        (t.bullets || []).slice(0, 2).forEach((b: string) => bullet(b));
        y += 2;
        if (t.quotes?.length) {
          doc.setFontSize(9).setFont("helvetica", "italic");
          (t.quotes as string[]).slice(0, 2).forEach((q: string) => {
            const qText = sanitize(`[Pasted review] "${q}"`);
            const lines = doc.splitTextToSize(qText, contentWidth - 20);
            ensureSpace(lines.length * 13);
            doc.text("\u2022", margin + 4, y);
            doc.text(lines, margin + 16, y);
            y += lines.length * 13;
          });
          doc.setFont("helvetica", "normal");
        }
        y += 8;
      });

      if ((pi.objections || []).length > 0) {
        ensureSpace(30);
        doc.setFontSize(13).setFont("helvetica", "bold");
        doc.text(sanitize("Top objections buyers express (pasted)"), margin, y);
        y += 20;
        doc.setFont("helvetica", "normal");
        (pi.objections || []).slice(0, 3).forEach((o: any) => {
          ensureSpace(70);
          doc.setFontSize(10).setFont("helvetica", "bold");
          doc.text("Objection:", margin, y);
          doc.setFont("helvetica", "normal");
          const objLines = doc.splitTextToSize(sanitize(o.objection), contentWidth - 80);
          doc.text(objLines, margin + 70, y);
          y += objLines.length * 14 + 4;
          doc.setFont("helvetica", "bold");
          doc.text("Reassurance:", margin, y);
          doc.setFont("helvetica", "normal");
          const resLines = doc.splitTextToSize(sanitize(o.reassurance), contentWidth - 80);
          doc.text(resLines, margin + 80, y);
          y += resLines.length * 14 + 12;
        });
      }
    }

    // Themes (dynamic heading)
    const themes = report.themes || [];
    const pdfThemeType = pack?.theme_type || "pain_themes";
    const themeHeading = pdfThemeType === "positioning_themes"
      ? `Top ${themes.length} Positioning Themes`
      : pdfThemeType === "market_themes"
      ? `Top ${themes.length} Market Themes`
      : `Top ${themes.length} Pain Themes`;
    heading(themeHeading);
    themes.forEach((t: any, i: number) => {
      ensureSpace(50);
      doc.setFontSize(12).setFont("helvetica", "bold");
      doc.text(sanitize(`${i + 1}. ${t.title}`), margin, y);
      y += 18;
      doc.setFont("helvetica", "normal");

      (t.bullets || []).forEach((b: string) => bullet(b));
      y += 4;

      if (t.snippets?.length) {
        doc.setFontSize(9).setFont("helvetica", "italic");
        (t.snippets as any[]).forEach((s) => {
          const text = typeof s === 'string' ? s : s.text;
          const label = typeof s === 'object' && s.label ? s.label : null;
          const prefix = label ? `[${label}] ` : '';
          const sText = sanitize(`${prefix}"${text}"`);
          const lines = doc.splitTextToSize(sText, contentWidth - 20);
          ensureSpace(lines.length * 13);
          doc.text("\u2022", margin + 4, y);
          doc.text(lines, margin + 16, y);
          y += lines.length * 13;
        });
        doc.setFont("helvetica", "normal");
      }

      if (t.messaging_angle) {
        ensureSpace(30);
        doc.setFontSize(9).setFont("helvetica", "italic");
        const angleText = sanitize(`Messaging angle: ${t.messaging_angle}`);
        const angleLines = doc.splitTextToSize(angleText, contentWidth - 10);
        doc.text(angleLines, margin + 8, y);
        y += angleLines.length * 13 + 4;
        doc.setFont("helvetica", "normal");
      }
      if (t.evidence_strength) {
        ensureSpace(16);
        doc.setFontSize(8).setFont("helvetica", "italic");
        const evText = sanitize(`Evidence strength: ${t.evidence_strength}`);
        doc.text(evText, margin + 8, y);
        y += 12;
        doc.setFont("helvetica", "normal");
      }
      y += 10;
    });

    // Seller Claims
    const sellerClaims = report.seller_claims_examples || [];
    if (sellerClaims.length > 0) {
      sectionBreak();
      heading("Seller Claims Spotted on Listings");
      doc.setFontSize(9).setFont("helvetica", "italic");
      const noteLines = doc.splitTextToSize(sanitize("Common marketing phrases found in competitor listing copy -- not customer quotes."), contentWidth);
      doc.text(noteLines, margin, y);
      y += noteLines.length * 13 + 4;
      doc.setFont("helvetica", "normal");
      sellerClaims.forEach((c: string) => bullet(c));
      y += 4;
    }

    // Desired Outcomes
    sectionBreak();
    heading("Desired Outcomes");
    (report.desired_outcomes || []).forEach((o: string) => bullet(o));
    y += 4;

    // Objections
    sectionBreak();
    heading("Objections & Reassurance");
    const objections = report.objections || [];
    if (report.review_evidence_available === false) {
      doc.setFontSize(9).setFont("helvetica", "italic");
      const subtitle = sanitize("Based on listing claims (not verified customer quotes)");
      const subLines = doc.splitTextToSize(subtitle, contentWidth);
      doc.text(subLines, margin, y);
      y += subLines.length * 13 + 4;
      doc.setFont("helvetica", "normal");
    }
    objections.forEach((o: any) => {
      ensureSpace(70);
      doc.setFontSize(10).setFont("helvetica", "bold");
      doc.text("Objection:", margin, y);
      doc.setFont("helvetica", "normal");
      const objLines = doc.splitTextToSize(sanitize(o.objection), contentWidth - 80);
      doc.text(objLines, margin + 70, y);
      y += objLines.length * 14 + 4;

      doc.setFont("helvetica", "bold");
      doc.text("Reassurance:", margin, y);
      doc.setFont("helvetica", "normal");
      const resLines = doc.splitTextToSize(sanitize(o.reassurance), contentWidth - 80);
      doc.text(resLines, margin + 80, y);
      y += resLines.length * 14 + 12;
    });

    // Copy Pack
    if (pack) {
      sectionBreak();
      heading("Conversion Copy Pack", 18);

      heading("Title Options", 13);
      (pack.titles || []).forEach((t: string) => bullet(t));
      y += 6;

      heading("Bullets", 13);
      (pack.bullets || []).forEach((b: string) => bullet(b));
      y += 6;

      heading("Description", 13);
      paragraph(pack.description || "");
      y += 6;

      heading("FAQs", 13);
      (pack.faqs || []).forEach((f: any) => {
        ensureSpace(40);
        doc.setFontSize(10).setFont("helvetica", "bold");
        const qLines = doc.splitTextToSize(sanitize(`Q: ${f.q}`), contentWidth);
        doc.text(qLines, margin, y);
        y += qLines.length * 14;
        doc.setFont("helvetica", "normal");
        const aLines = doc.splitTextToSize(sanitize(`A: ${f.a}`), contentWidth);
        ensureSpace(aLines.length * 14);
        doc.text(aLines, margin, y);
        y += aLines.length * 14 + 8;
      });

      heading("Ad Hooks / Headlines", 13);
      (pack.ad_hooks || []).forEach((h: string) => bullet(h));
    }
  }

  // ── Sources ──
  sectionBreak();
  const sourcesTitle = hasWebEvidence && report.review_evidence_available !== false
    ? "Real user evidence (web)"
    : "Sources";
  heading(sourcesTitle);
  (report.sources || []).forEach((s: string, i: number) => {
    ensureSpace(28);

    // Build domain + short title
    let domain = "";
    let shortTitle = "";
    const asinMatch = s.match(/(?:\/dp\/|\/gp\/product\/)([A-Z0-9]{10})/i);
    try {
      const urlObj = new URL(s);
      domain = urlObj.hostname.replace("www.", "");
      if (asinMatch) {
        shortTitle = `ASIN: ${asinMatch[1]}`;
      } else {
        const pathSegments = urlObj.pathname.split("/").filter(Boolean);
        shortTitle = pathSegments.length > 0
          ? decodeURIComponent(pathSegments[pathSegments.length - 1]).replace(/[-_]/g, " ").slice(0, 50)
          : "";
      }
    } catch {
      domain = s.slice(0, 40);
    }

    const sourceEntry = evidenceSourcesUsed.find((e: any) => e.url === s);
    const categoryLabel = sourceEntry?.category === "user_voice"
      ? (sourceEntry?.comments_extracted === false ? " [User voice (limited)]" : " [User voice]")
      : sourceEntry?.category === "editorial" ? " [Editorial]" : "";

    // Line 1: domain + short title (10pt bold)
    const label = shortTitle
      ? `${i + 1}. ${domain} — ${shortTitle}${categoryLabel}`
      : `${i + 1}. ${domain}${categoryLabel}`;
    doc.setFontSize(10).setFont("helvetica", "bold");
    const labelLines = doc.splitTextToSize(sanitize(label), contentWidth);
    doc.text(labelLines, margin, y);
    y += labelLines.length * 13;

    // Line 2: full URL (7pt grey, indented)
    doc.setFontSize(7).setFont("helvetica", "normal").setTextColor(120, 120, 120);
    const urlLines = doc.splitTextToSize(sanitize(s), contentWidth - 16);
    doc.text(urlLines, margin + 8, y);
    y += urlLines.length * 10 + 4;

    // Reset text color
    doc.setTextColor(0, 0, 0);
  });

  // ── Footer ──
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(7).setFont("helvetica", "normal").setTextColor(100);
    doc.textWithLink("Give feedback: https://forms.gle/7JHiUT56vXBUQuTb9", margin, pageHeight - 27, {
      url: "https://forms.gle/7JHiUT56vXBUQuTb9"
    });
    doc.setFontSize(8).setTextColor(150);
    doc.text("Generated by ScopSift", margin, pageHeight - 20);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 20, { align: "right" });
    doc.setTextColor(0);
  }

  const filename = `scopsift-${report.product_category.replace(/\s+/g, "-").toLowerCase()}-${new Date(report.created_at).toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
}
